import { Reorder } from 'motion/react'
import { useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

import { FavoriteUnavailable } from '@root/features/favorite-pairs/model/favorite-unavailable'
import { FadeOverflow, hiddenScrollbarCss } from '@root/shared/ui-kit'
import { HorizontalList } from '@root/shared/ui-kit/horizontal-list'

import { TopbarFavoritesItem } from './topbar-favorites-item'

import type { PairAddress, PairModel } from '@root/entities/pair'

type Props = {
  favoritePairs: Array<PairModel | FavoriteUnavailable>
  onReorder: (newOrder: Array<PairModel | FavoriteUnavailable>) => void
}

export const TopbarFavoritesList = ({ favoritePairs, onReorder }: Props) => {
  const [order, setOrder] = useState(favoritePairs.map((x) => x.address))
  const pairMap = useMemo(
    () => new Map(favoritePairs.map((x) => [x.address, x])),
    [favoritePairs],
  )
  const scrollRef = useRef<HTMLDivElement>(null)

  const handleReorder = (newOrder: Array<PairAddress>) => {
    const newPairsOrder = newOrder
      .map((pairAddress) => pairMap.get(pairAddress))
      .filter((x) => !!x)

    setOrder(newOrder)
    onReorder(newPairsOrder)
  }

  return (
    <HorizontalList transformScrollBehavior scrollRef={scrollRef}>
      <ListGroup>
        <Reorder.Group
          ref={scrollRef}
          className="reorder-group"
          as="div"
          axis="x"
          layoutScroll
          style={{ overflowX: 'scroll' }}
          values={order}
          onReorder={(newOrder) => handleReorder(newOrder)}
        >
          {order
            .map((address) => {
              const pair = pairMap.get(address)
              if (pair instanceof FavoriteUnavailable) {
                return undefined
              }

              return pair ? (
                <TopbarFavoritesItem key={address} pair={pair} />
              ) : undefined
            })
            .filter((x) => !!x)}
        </Reorder.Group>
      </ListGroup>
    </HorizontalList>
  )
}

const ListGroup = styled(FadeOverflow)`
  overflow: hidden;
  padding-inline: 2px 10px;

  & > .reorder-group {
    position: relative;
    display: flex;
    width: 100%;
    overflow-y: hidden;

    ${hiddenScrollbarCss}

    html[data-platform='windows'] & {
      scrollbar-color: unset;
      scrollbar-width: auto;
      &::-webkit-scrollbar {
        display: block;
        height: 8px;
      }
    }
  }
`
